/**
 * Svelte store for subscription state management
 */

import { writable, derived, get } from 'svelte/store';
import type { UnifiedSubscriptionStatus } from '$lib/types/subscription';
import { SubscriptionService } from '$lib/services/subscription/SubscriptionService';
import { ServerLicenseService } from '$lib/services/license/ServerLicenseService';
import { SUBSCRIPTION_LIMITS } from '$lib/constants/subscription';

/**
 * Create subscription store
 */
function createSubscriptionStore() {
  const service = SubscriptionService.getInstance();
  const licenseService = new ServerLicenseService();
  const { subscribe, set, update } = writable<UnifiedSubscriptionStatus>(service.getStatus());
  
  // Subscribe to service changes
  service.onStatusChange((status) => {
    set(status);
  });

  return {
    subscribe,
    
    /**
     * Refresh subscription status
     */
    async refresh() {
      // Force service to update its status
      await service.updateSubscriptionStatus();
      const newStatus = service.getStatus();
      set(newStatus);
      return newStatus;
    },
    
    /**
     * Activate license (using server-based validation)
     */
    async activateLicense(licenseKey: string): Promise<boolean> {
      try {
        // Get device info
        const deviceInfo = await licenseService.getCurrentDeviceInfo();
        
        // Activate through server
        const result = await licenseService.activateLicense(licenseKey, deviceInfo);
        
        if (result.success && result.license) {
          // Store the full license info in the secure storage system
          // Use the same storage instance that SubscriptionService uses
          const { LicenseServiceFactory } = await import('$lib/services/license/LicenseServiceFactory');
          const licenseServices = LicenseServiceFactory.createLicenseServices();
          await licenseServices.storage.storeLicense(licenseKey, result.license);
          
          // End trial if active since we now have premium
          const { TrialService } = await import('$lib/services/subscription/TrialService');
          const trialService = TrialService.getInstance();
          if (trialService.isActive()) {
            trialService.endTrial();
          }
          
          // Force immediate status update
          await service.updateSubscriptionStatus();
          
          // Wait a moment for the update to complete
          await new Promise(resolve => setTimeout(resolve, 200));
          
          // Get and set the new status
          const newStatus = service.getStatus();
          
          // If still not premium, force it
          if (newStatus.type !== 'premium') {
            const premiumStatus = {
              ...newStatus,
              type: 'premium' as const,
              isActive: true
            };
            set(premiumStatus);
          } else {
            set(newStatus);
          }
          
          return true;
        }
        
        console.error('License activation failed:', result.error);
        return false;
      } catch (error) {
        console.error('License activation error:', error);
        return false;
      }
    },
    
    /**
     * Check if user can perform action
     */
    async canPerformAction(action: 'spellCheck' | 'aiAnalysis'): Promise<boolean> {
      return await service.canPerformAction(action);
    },
    
    /**
     * Check text length limit
     */
    isWithinTextLimit(textLength: number): boolean {
      return service.isWithinTextLimit(textLength);
    },
    
    /**
     * Record usage
     */
    async recordUsage(type: 'spellCheck' | 'aiAnalysis') {
      await service.recordUsage(type);
      await this.refresh();
    },
    
    /**
     * Get remaining uses
     */
    getRemainingUses(type: 'spellCheck' | 'aiAnalysis'): number {
      return service.getRemainingUses(type);
    },
    
    /**
     * Get formatted status
     */
    getFormattedStatus(): string {
      return service.formatStatus();
    },
    
    /**
     * Get upgrade benefits
     */
    getUpgradeBenefits(): string[] {
      return service.getUpgradeBenefits();
    },
    
    /**
     * Reset subscription (for development)
     */
    async reset() {
      await service.resetSubscription();
      await this.refresh();
    }
  };
}

// Main subscription store
export const subscriptionStore = createSubscriptionStore();

// Derived stores for convenient access
export const subscriptionType = derived(
  subscriptionStore,
  $subscription => $subscription?.type || 'free'
);

export const isPremium = derived(
  subscriptionStore,
  $subscription => $subscription?.type === 'premium'
);

export const isInTrial = derived(
  subscriptionStore,
  $subscription => $subscription?.type === 'trial'
);

export const isFreeUser = derived(
  subscriptionStore,
  $subscription => $subscription?.type === 'free'
);

export const canUseAI = derived(
  subscriptionStore,
  $subscription => $subscription?.features?.aiAnalysis || false
);

export const dailyUsage = derived(
  subscriptionStore,
  $subscription => $subscription?.currentDailyUsage || { spellChecks: 0, aiAnalyses: 0 }
);

export const usageLimits = derived(
  subscriptionStore,
  $subscription => ({
    spellCheck: $subscription?.dailySpellCheckLimit || SUBSCRIPTION_LIMITS.FREE.dailySpellCheckLimit,
    aiAnalysis: $subscription?.dailyAIAnalysisLimit || SUBSCRIPTION_LIMITS.FREE.dailyAIAnalysisLimit,
    textLength: $subscription?.maxTextLength || SUBSCRIPTION_LIMITS.FREE.maxTextLength
  })
);

export const trialInfo = derived(
  subscriptionStore,
  $subscription => $subscription?.trial || null
);

export const trialDaysRemaining = derived(
  subscriptionStore,
  $subscription => $subscription?.trial?.daysRemaining ?? 0
);

// Helper functions
export async function checkSubscriptionLimit(action: 'spellCheck' | 'aiAnalysis'): Promise<boolean> {
  return await subscriptionStore.canPerformAction(action);
}

export function checkTextLimit(textLength: number): boolean {
  return subscriptionStore.isWithinTextLimit(textLength);
}

export async function recordSubscriptionUsage(type: 'spellCheck' | 'aiAnalysis'): Promise<void> {
  await subscriptionStore.recordUsage(type);
}

export function getRemainingUsage(type: 'spellCheck' | 'aiAnalysis'): number {
  return subscriptionStore.getRemainingUses(type);
}

/**
 * Modal control stores (legacy - kept for backward compatibility)
 */
export const showPricingModal = writable(false);
export const showUpgradePrompt = writable(false);
export const upgradePromptContext = writable<{
  reason: 'limit_reached' | 'trial_expiring' | 'trial_expired' | 'feature_locked' | 'text_too_long' | 'ai_feature_limit';
  details?: string;
}>({ reason: 'limit_reached' });


/**
 * Legacy functions - maintained for backward compatibility
 */
export function openPricingModal(context?: { reason: string; details?: string }) {
  if (context) {
    upgradePromptContext.set({
      reason: context.reason as any,
      details: context.details
    });
  }
  showPricingModal.set(true);
}

export function openUpgradePrompt(reason: 'limit_reached' | 'trial_expiring' | 'trial_expired' | 'feature_locked' | 'text_too_long', details?: string) {
  upgradePromptContext.set({ reason, details });
  showUpgradePrompt.set(true);
}

export function closeSubscriptionModals() {
  showPricingModal.set(false);
  showUpgradePrompt.set(false);
}